import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useInView,
} from "framer-motion";
import { Link } from "react-router-dom";
import Header from "components/layout/Header";
import Footer from "components/layout/Footer";
import IntroAnimation from "components/IntroAnimation";
import { usePublishedBlogs } from "../../hooks/useBlogs";
import { useHomeContent } from "../../hooks/useHomeContent";
import { useScrollToHash } from "../../hooks/useScrollToHash";
import { CardSkeleton } from "../../components/Skeletons/CardSkeleton";
import { EmptyState } from "../../components/States/ErrorState";
import {
  ArrowRight,
  BookOpen,
  Clock,
  Sparkles,
  Palette,
  TrendingUp,
  Code2,
  Cpu,
  PenTool,
  Pen,
  CheckCircle2,
  Trophy,
  Users,
  Globe,
  Rocket,
  Search,
  Briefcase,
  Award,
  Lightbulb,
  Compass,
  BarChart3,
  Play,
  ArrowUpRight,
  ThumbsUp,
  Brain,
  Eye,
  Crosshair,
  FileText,
  Zap,
  Star,
  Target,
  Shield,
  CircleDot,
  Bot,
} from "lucide-react";

/* ═══════════════════════════ DATA ═══════════════════════════ */

const SERVICES = [
  {
    icon: Palette,
    title: "Branding & Identity",
    desc: "Your brand gets forgotten or remembered. We build the kind that sticks—logos that work everywhere, systems your team uses.",
    gradient: "from-violet-500 to-purple-600",
    light: "bg-violet-50",
    iconColor: "text-violet-600",
    tag: "Where we started",
    href: "/services#branding",
  },
  {
    icon: TrendingUp,
    title: "Digital Marketing",
    desc: "Understand your market. Reach the right people at the right time with messages that actually work.",
    gradient: "from-purple-500 to-indigo-600",
    light: "bg-purple-50",
    iconColor: "text-purple-600",
    href: "/services#marketing",
  },
  {
    icon: Code2,
    title: "Web & App Development",
    desc: "Apps built for every device, optimized for emerging markets. Slow internet? We handle it.",
    gradient: "from-indigo-500 to-blue-600",
    light: "bg-indigo-50",
    iconColor: "text-indigo-600",
    href: "/services#development",
  },
  {
    icon: PenTool,
    title: "UI/UX Design",
    desc: "Interfaces people actually understand. Research-backed, tested with real users.",
    gradient: "from-fuchsia-500 to-pink-600",
    light: "bg-fuchsia-50",
    iconColor: "text-fuchsia-600",
    href: "/services#design",
  },
  {
    icon: Cpu,
    title: "AI Automation",
    desc: "AI that understands context. Chatbots that read the room. Automation that adapts to how you work.",
    gradient: "from-violet-500 to-fuchsia-600",
    light: "bg-violet-50",
    iconColor: "text-violet-500",
    tag: "New",
    href: "/services#ai",
  },
  {
    icon: Pen,
    title: "Content Creation",
    desc: "Content that solves problems, not just tells stories. Every piece built to move people closer to action.",
    gradient: "from-purple-500 to-violet-600",
    light: "bg-purple-50",
    iconColor: "text-purple-500",
    href: "/services#content",
  },
];

const STATS = [
  { value: "150+", label: "Projects Delivered", icon: Trophy },
  { value: "50+", label: "Happy Clients", icon: Users },
  { value: "3", label: "Global Markets", icon: Globe },
  { value: "98%", label: "Satisfaction Rate", icon: ThumbsUp },
];

const PROCESS_STEPS = [
  {
    step: "01",
    title: "Discovery",
    desc: "We understand your brand, audience, and market to build a strategy that actually works.",
    icon: Search,
  },
  {
    step: "02",
    title: "Strategy",
    desc: "Data-backed planning across branding, marketing, development & AI.",
    icon: Briefcase,
  },
  {
    step: "03",
    title: "Execution",
    desc: "Our team brings the strategy to life with pixel-perfect precision.",
    icon: Rocket,
  },
  {
    step: "04",
    title: "Growth",
    desc: "Continuous optimization, analytics & iteration to keep you ahead.",
    icon: TrendingUp,
  },
];

const SHOWCASE = [
  {
    title: "Luxury Resort Rebrand",
    category: "Branding",
    desc: "Complete brand overhaul including logo, web presence, and marketing. Resulted in a 340% increase in bookings.",
    image:
      "https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=800&q=80",
    stat: "+340%",
    statLabel: "Bookings",
  },
  {
    title: "E-Commerce Growth Engine",
    category: "Marketing + AI",
    desc: "AI-powered marketing automation and full funnel optimization. Generated ₹2.5Cr revenue in 6 months.",
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&q=80",
    stat: "₹2.5Cr",
    statLabel: "Revenue",
  },
  {
    title: "FinTech Platform Launch",
    category: "Development",
    desc: "Full-stack web app with AI analytics dashboard. Acquired 100K users in 3 months.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
    stat: "100K",
    statLabel: "Users",
  },
];

const MARKETS = [
  {
    name: "India",
    badge: "🇮🇳",
    color: "from-orange-500 to-amber-500",
    desc: "Startups & enterprises across Tier 1, 2, and 3 cities",
    image:
      "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Middle East",
    badge: "🇦🇪",
    color: "from-rose-500 to-pink-500",
    desc: "Luxury, real estate, hospitality & enterprise",
    image:
      "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=600&q=80",
  },
  {
    name: "Southeast Asia",
    badge: "🇸🇬",
    color: "from-emerald-500 to-teal-500",
    desc: "Tech startups & digital commerce",
    image:
      "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?auto=format&fit=crop&w=600&q=80",
  },
];

const TESTIMONIALS = [
  {
    quote:
      "BusiGrow completely changed how we present ourselves online. The branding, website and marketing strategy actually work together.",
    name: "Arjun Kapoor",
    role: "CEO, TechVentures",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=150&q=80",
  },
  {
    quote:
      "Their AI automation reduced our support costs by 60%. The chatbot they built handles thousands of queries per day flawlessly.",
    name: "Sara Al-Sheikh",
    role: "COO, Gulf Properties",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=150&q=80",
  },
  {
    quote:
      "Best design team we've ever worked with. The UI/UX overhaul doubled our conversion rates practically overnight.",
    name: "Priya Menon",
    role: "Founder, StyleBox",
    rating: 5,
    avatar:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=150&q=80",
  },
];

const DEFAULT_ANTI_BUBBLE_SETTINGS = {
  eyebrow: "Why Trust BusiGrow",
  headingPrefix: "We Don't Follow the",
  headingHighlight: "AI Bubble",
  headingSuffix: "We Burst It.",
  description:
    "While others chase trends, we analyze, strategize, and build solutions rooted in real understanding. Not hype. Not templates. Pure, purpose-driven growth.",
  taglineTop: "We're outside the bubble.",
  taglineBottom: "We pop it.",
  bubbleLabels: [
    "GPT",
    "AI",
    "Hype",
    "LLM",
    "Bot",
    "Buzz",
    "Trend",
    "Auto",
    "ML",
    "Gen",
    "Prompt",
    "Token",
    "AGI",
    "Fine-tune",
    "RAG",
    "Vector",
    "Neural",
    "Model",
  ],
  burstIntervalMs: 3200,
  burstDurationMs: 900,
};

const FALLBACK_BLOG_IMAGE =
  "https://images.unsplash.com/photo-1504711434969-e33886168d8c?auto=format&fit=crop&w=900&q=80";

/* ════════════════════ UTILITY COMPONENTS ════════════════════ */

/* Animated number counter */
function AnimatedCounter({ value, suffix = "", prefix = "", duration = 2 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const num = parseInt(value.replace(/[^0-9]/g, ""), 10);
    if (isNaN(num)) {
      setDisplay(value);
      return;
    }
    let start = 0;
    const step = num / (duration * 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= num) {
        setDisplay(prefix + num.toLocaleString() + suffix);
        clearInterval(timer);
      } else setDisplay(prefix + Math.floor(start).toLocaleString() + suffix);
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, value, duration, prefix, suffix]);

  return <span ref={ref}>{display}</span>;
}

/* Magnetic hover card wrapper */
function MagneticCard({ children, className = "" }) {
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 20 });
  const springY = useSpring(y, { stiffness: 300, damping: 20 });

  const handleMouse = useCallback(
    (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      x.set((e.clientX - rect.left - rect.width / 2) * 0.05);
      y.set((e.clientY - rect.top - rect.height / 2) * 0.05);
    },
    [x, y],
  );

  return (
    <motion.div
      style={{ x: springX, y: springY }}
      onMouseMove={handleMouse}
      onMouseLeave={() => {
        x.set(0);
        y.set(0);
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function FloatingOrbs() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute -top-20 -left-20 w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full bg-gradient-to-br from-violet-200/40 to-purple-200/20 blur-3xl animate-float-orb" />
      <div className="absolute top-1/3 -right-32 w-[250px] md:w-[400px] h-[250px] md:h-[400px] rounded-full bg-gradient-to-bl from-purple-200/30 to-fuchsia-200/20 blur-3xl animate-float-orb-reverse" />
      <div
        className="absolute -bottom-20 left-1/3 w-[200px] md:w-[350px] h-[200px] md:h-[350px] rounded-full bg-gradient-to-tr from-indigo-200/25 to-violet-200/15 blur-3xl animate-float-orb"
        style={{ animationDelay: "-7s" }}
      />
    </div>
  );
}

/* ════════════════ ELECTRICAL CIRCUIT BACKGROUND ════════════════ */

function ElectricalCircuitBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animFrame;
    let time = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth * (window.devicePixelRatio || 1);
      canvas.height = canvas.offsetHeight * (window.devicePixelRatio || 1);
      ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1);
    };
    resize();
    window.addEventListener("resize", resize);

    const W = () => canvas.offsetWidth;
    const H = () => canvas.offsetHeight;

    // Circuit nodes positioned as a grid with some randomness
    const NODES = [];
    const COLS = 12;
    const ROWS = 8;
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        NODES.push({
          x: (c + 0.5) / COLS + (Math.random() - 0.5) * 0.04,
          y: (r + 0.5) / ROWS + (Math.random() - 0.5) * 0.04,
          pulseOffset: Math.random() * Math.PI * 2,
          pulseSpeed: 0.5 + Math.random() * 1.5,
          size: 1 + Math.random() * 1.5,
        });
      }
    }

    // Build connections between nearby nodes
    const CONNECTIONS = [];
    for (let i = 0; i < NODES.length; i++) {
      for (let j = i + 1; j < NODES.length; j++) {
        const dx = NODES[i].x - NODES[j].x;
        const dy = NODES[i].y - NODES[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 0.18 && Math.random() > 0.3) {
          CONNECTIONS.push({
            from: i,
            to: j,
            pulseOffset: Math.random() * Math.PI * 2,
            pulseSpeed: 0.3 + Math.random() * 0.7,
            hasEnergy: Math.random() > 0.5,
          });
        }
      }
    }

    // Energy particles that flow along connections
    const PARTICLES = [];
    for (let i = 0; i < 20; i++) {
      const conn = CONNECTIONS[Math.floor(Math.random() * CONNECTIONS.length)];
      PARTICLES.push({
        conn,
        t: Math.random(),
        speed: 0.002 + Math.random() * 0.004,
        size: 1.5 + Math.random() * 2,
        glow: 4 + Math.random() * 8,
      });
    }

    function draw() {
      time += 0.016;
      const w = W();
      const h = H();

      ctx.clearRect(0, 0, w, h);

      // Draw connections
      CONNECTIONS.forEach((conn) => {
        const a = NODES[conn.from];
        const b = NODES[conn.to];
        const ax = a.x * w;
        const ay = a.y * h;
        const bx = b.x * w;
        const by = b.y * h;

        const pulse =
          0.03 + 0.04 * Math.sin(time * conn.pulseSpeed + conn.pulseOffset);

        // Draw connection line — circuit-style (right-angle paths)
        ctx.beginPath();
        const midX = (ax + bx) / 2;
        ctx.moveTo(ax, ay);

        // Create L-shaped or straight connections
        if (Math.abs(ax - bx) > 20 && Math.abs(ay - by) > 20) {
          if (conn.from % 2 === 0) {
            ctx.lineTo(midX, ay);
            ctx.lineTo(midX, by);
          } else {
            ctx.lineTo(ax, (ay + by) / 2);
            ctx.lineTo(bx, (ay + by) / 2);
          }
        }
        ctx.lineTo(bx, by);

        ctx.strokeStyle = `rgba(139, 92, 246, ${pulse})`;
        ctx.lineWidth = 0.5;
        ctx.stroke();

        // Glow on active connections
        if (conn.hasEnergy) {
          ctx.strokeStyle = `rgba(139, 92, 246, ${pulse * 0.5})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });

      // Draw nodes
      NODES.forEach((node) => {
        const nx = node.x * w;
        const ny = node.y * h;
        const pulse =
          0.2 + 0.3 * Math.sin(time * node.pulseSpeed + node.pulseOffset);

        // Glow
        const grad = ctx.createRadialGradient(nx, ny, 0, nx, ny, node.size * 4);
        grad.addColorStop(0, `rgba(139, 92, 246, ${pulse * 0.6})`);
        grad.addColorStop(1, "rgba(139, 92, 246, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(nx, ny, node.size * 4, 0, Math.PI * 2);
        ctx.fill();

        // Core dot
        ctx.fillStyle = `rgba(139, 92, 246, ${0.3 + pulse * 0.4})`;
        ctx.beginPath();
        ctx.arc(nx, ny, node.size, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw flowing energy particles
      PARTICLES.forEach((p) => {
        p.t += p.speed;
        if (p.t > 1) {
          p.t = 0;
          p.conn = CONNECTIONS[Math.floor(Math.random() * CONNECTIONS.length)];
        }

        const a = NODES[p.conn.from];
        const b = NODES[p.conn.to];
        const px = (a.x + (b.x - a.x) * p.t) * w;
        const py = (a.y + (b.y - a.y) * p.t) * h;

        // Bright glow
        const grad = ctx.createRadialGradient(px, py, 0, px, py, p.glow);
        grad.addColorStop(0, "rgba(167, 139, 250, 0.8)");
        grad.addColorStop(0.3, "rgba(139, 92, 246, 0.3)");
        grad.addColorStop(1, "rgba(139, 92, 246, 0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(px, py, p.glow, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
        ctx.beginPath();
        ctx.arc(px, py, p.size * 0.5, 0, Math.PI * 2);
        ctx.fill();
      });

      animFrame = requestAnimationFrame(draw);
    }

    draw();

    return () => {
      cancelAnimationFrame(animFrame);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.45 }}
    />
  );
}

function Section({ children, className = "", id }) {
  return (
    <motion.section
      id={id}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`relative py-8 sm:py-12 md:py-16 ${className}`}
    >
      {children}
    </motion.section>
  );
}

/* ════════════════ HERO DASHBOARD MOCKUP ════════════════ */

function HeroMockup() {
  const [hoveredBar, setHoveredBar] = useState(null);
  const barData = [
    { h: 35, label: "Jan", val: "₹2.1L" },
    { h: 55, label: "Feb", val: "₹3.3L" },
    { h: 40, label: "Mar", val: "₹2.4L" },
    { h: 70, label: "Apr", val: "₹4.2L" },
    { h: 50, label: "May", val: "₹3.0L" },
    { h: 85, label: "Jun", val: "₹5.1L" },
    { h: 65, label: "Jul", val: "₹3.9L" },
    { h: 92, label: "Aug", val: "₹5.5L" },
    { h: 75, label: "Sep", val: "₹4.5L" },
    { h: 95, label: "Oct", val: "₹5.7L" },
    { h: 82, label: "Nov", val: "₹4.9L" },
    { h: 62, label: "Dec", val: "₹3.7L" },
  ];

  return (
    <div
      className="relative w-full max-w-lg mx-auto"
      style={{ perspective: "1200px" }}
    >
      {/* Main dashboard card */}
      <motion.div
        initial={{ opacity: 0, y: 40, rotateY: -8, rotateX: 4 }}
        animate={{ opacity: 1, y: 0, rotateY: 0, rotateX: 0 }}
        transition={{ delay: 0.6, duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative glass-strong rounded-3xl p-5 shadow-apple-xl border border-violet-100/50"
      >
        {/* Browser chrome */}
        <div className="flex items-center gap-2 mb-4">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
          <div className="flex-1 mx-3">
            <div className="h-5 rounded-full bg-slate-100/80 flex items-center px-3">
              <span className="text-[9px] text-slate-400 font-medium">
                busigrow.in/dashboard
              </span>
            </div>
          </div>
        </div>

        {/* Chart header */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-[11px] font-semibold text-slate-700">
            Revenue Overview
          </span>
          <motion.span
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="text-[10px] text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded-full"
          >
            +22% ↑
          </motion.span>
        </div>

        {/* Interactive chart bars with tooltips */}
        <div className="flex items-end gap-1.5 h-28 mb-4 relative">
          {barData.map((bar, i) => (
            <div
              key={i}
              className="flex-1 relative flex flex-col items-center justify-end h-full"
            >
              <AnimatePresence>
                {hoveredBar === i && (
                  <motion.div
                    initial={{ opacity: 0, y: 5, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 5, scale: 0.8 }}
                    className="absolute -top-8 z-20 bg-slate-800 text-white text-[8px] font-bold px-2 py-1 rounded-lg whitespace-nowrap shadow-lg"
                  >
                    {bar.val}
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-[4px] border-r-[4px] border-t-[4px] border-transparent border-t-slate-800" />
                  </motion.div>
                )}
              </AnimatePresence>
              <motion.div
                initial={{ height: 0 }}
                animate={{ height: `${bar.h}%` }}
                transition={{
                  delay: 1.2 + i * 0.06,
                  duration: 0.6,
                  ease: "easeOut",
                }}
                whileHover={{ scaleY: 1.08 }}
                onHoverStart={() => setHoveredBar(i)}
                onHoverEnd={() => setHoveredBar(null)}
                className={`w-full rounded-md cursor-pointer transition-all duration-200 ${
                  hoveredBar === i
                    ? "bg-gradient-to-t from-violet-600 to-purple-400 shadow-[0_0_12px_rgba(139,92,246,0.4)]"
                    : i >= 9
                      ? "bg-gradient-to-t from-violet-600 to-purple-400"
                      : "bg-gradient-to-t from-violet-300/60 to-violet-100/40"
                }`}
                style={{ originY: 1 }}
              />
            </div>
          ))}
        </div>

        {/* Animated line chart overlay */}
        <svg
          className="absolute left-5 right-5 h-28 pointer-events-none"
          style={{ top: "106px" }}
          viewBox="0 0 400 112"
          preserveAspectRatio="none"
        >
          <motion.path
            d={`M0,${112 - 35 * 1.12} ${barData.map((b, i) => `L${(i * 400) / 11},${112 - b.h * 1.12}`).join(" ")}`}
            fill="none"
            stroke="url(#lineGrad)"
            strokeWidth="2"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ delay: 2, duration: 1.5, ease: "easeInOut" }}
          />
          <defs>
            <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(139,92,246,0.3)" />
              <stop offset="100%" stopColor="rgba(139,92,246,0.8)" />
            </linearGradient>
          </defs>
        </svg>

        {/* Metrics row */}
        <div className="grid grid-cols-3 gap-2">
          {[
            {
              label: "Revenue",
              value: "₹12.5L",
              change: "+22%",
              icon: TrendingUp,
            },
            {
              label: "Active Leads",
              value: "1,247",
              change: "+18%",
              icon: Users,
            },
            { label: "Conversion", value: "4.8%", change: "+12%", icon: Zap },
          ].map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.9 + i * 0.12 }}
              whileHover={{ scale: 1.05, y: -2 }}
              className="p-2.5 rounded-xl bg-violet-50/60 border border-violet-100/30 cursor-default hover:bg-violet-100/60 transition-colors"
            >
              <div className="flex items-center justify-between">
                <p className="text-[9px] text-slate-400 font-medium">
                  {m.label}
                </p>
                <m.icon className="w-3 h-3 text-violet-400" />
              </div>
              <p className="text-sm font-bold text-slate-900 mt-0.5">
                {m.value}
              </p>
              <p className="text-[9px] font-bold text-green-600 mt-0.5">
                {m.change}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Live pulse indicator */}
        <motion.div
          className="absolute top-3 right-3 flex items-center gap-1.5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5 }}
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          <span className="text-[8px] text-green-600 font-semibold">LIVE</span>
        </motion.div>
      </motion.div>

      {/* Floating satellite — Growth card */}
      <motion.div
        initial={{ opacity: 0, x: -40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 2.2, duration: 0.6 }}
        whileHover={{ scale: 1.08, rotate: -2 }}
        className="absolute -left-16 top-1/4 glass-strong rounded-2xl p-3 shadow-apple-lg animate-float-slow z-10 cursor-default"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-sm">
            <TrendingUp className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 font-medium">
              Monthly Growth
            </p>
            <p className="text-sm font-bold text-slate-900">+34.2%</p>
          </div>
        </div>
      </motion.div>

      {/* Floating satellite — AI Agent card */}
      <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 2.5, duration: 0.6 }}
        whileHover={{ scale: 1.08, rotate: 2 }}
        className="absolute -right-10 bottom-1/3 glass-strong rounded-2xl p-3 shadow-apple-lg animate-float-slow z-10 cursor-default"
        style={{ animationDelay: "-3s" }}
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-[10px] text-slate-500 font-medium">AI Agent</p>
            <p className="text-xs font-bold text-green-600 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
              Active
            </p>
          </div>
        </div>
      </motion.div>

      {/* Floating satellite — Client avatars */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2.8, duration: 0.6 }}
        whileHover={{ scale: 1.08 }}
        className="absolute -right-4 -top-4 glass-strong rounded-2xl p-2.5 shadow-apple animate-float-slow z-10 cursor-default"
        style={{ animationDelay: "-5s" }}
      >
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {TESTIMONIALS.map((t, i) => (
              <img
                key={i}
                src={t.avatar}
                alt=""
                className="w-6 h-6 rounded-full border-2 border-white object-cover"
              />
            ))}
          </div>
          <span className="text-[10px] font-bold text-slate-700">
            50+ clients
          </span>
        </div>
      </motion.div>
    </div>
  );
}

/* ════════════════ INTERACTIVE BUBBLE ════════════════ */

function InteractiveBubble() {
  const [popped, setPopped] = useState(false);
  const [particles, setParticles] = useState([]);

  const handlePop = () => {
    if (popped) return;
    // Generate burst particles
    const newParticles = Array.from({ length: 16 }, (_, i) => ({
      id: i,
      angle: (i * 360) / 16,
      distance: 40 + Math.random() * 60,
      size: 3 + Math.random() * 6,
      color: ["#8b5cf6", "#7c3aed", "#a78bfa", "#c4b5fd", "#6d28d9", "#ddd6fe"][
        Math.floor(Math.random() * 6)
      ],
    }));
    setParticles(newParticles);
    setPopped(true);
    // Reset after 4 seconds
    setTimeout(() => {
      setPopped(false);
      setParticles([]);
    }, 4000);
  };

  return (
    <div
      className="relative flex items-center justify-center mt-4"
      style={{ height: 120 }}
    >
      <AnimatePresence mode="wait">
        {!popped ? (
          <motion.button
            key="bubble"
            initial={{ scale: 0 }}
            animate={{ scale: [0.9, 1.05, 0.97, 1] }}
            exit={{ scale: [1, 1.3, 0], opacity: [1, 0.8, 0] }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            onClick={handlePop}
            className="relative w-20 h-20 rounded-full cursor-pointer focus:outline-none group"
            style={{
              background:
                "radial-gradient(circle at 35% 35%, rgba(255,255,255,0.5), rgba(139,92,246,0.2) 50%, rgba(124,58,237,0.1))",
              border: "1.5px solid rgba(167,139,250,0.4)",
              boxShadow:
                "0 0 30px rgba(139,92,246,0.2), inset 0 0 20px rgba(255,255,255,0.3)",
            }}
            whileHover={{ scale: 1.12 }}
            whileTap={{ scale: 0.9 }}
          >
            {/* Reflection */}
            <div
              className="absolute top-2 left-3 w-6 h-3 rounded-full bg-white/40 blur-[1px]"
              style={{ transform: "rotate(-25deg)" }}
            />
            {/* Inner shimmer */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                background:
                  "linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.3) 50%, transparent 70%)",
              }}
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            />
            <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-violet-500/60 group-hover:text-violet-600">
              Tap me!
            </span>
          </motion.button>
        ) : (
          <motion.div
            key="message"
            initial={{ opacity: 0, scale: 0.5, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
            className="text-center"
          >
            <p className="text-sm font-bold text-gradient-vibrant">
              Built by BusiGrow
            </p>
            <p className="text-[10px] text-slate-400 mt-1">
              Your growth partner
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Burst particles */}
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            background: p.color,
            top: "50%",
            left: "50%",
          }}
          initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
          animate={{
            x: Math.cos((p.angle * Math.PI) / 180) * p.distance,
            y: Math.sin((p.angle * Math.PI) / 180) * p.distance,
            scale: [1, 1.5, 0],
            opacity: [1, 0.8, 0],
          }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      ))}
    </div>
  );
}

/* ════════════════ OFFER RIBBON ════════════════ */

function OfferRibbon() {
  const offers = [
    "25% OFF Branding",
    "Free AI Audit",
    "Free Strategy Call",
    "Studio is Live",
  ];

  const duplicated = [...offers, ...offers, ...offers, ...offers];

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="relative bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 text-white overflow-hidden rounded-full mx-4 sm:mx-10 md:mx-auto md:max-w-3xl shadow-apple-lg my-2"
      style={{ height: 40 }}
    >
      {/* Fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-violet-600 to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-indigo-600 to-transparent z-10 pointer-events-none" />

      <div className="h-full flex items-center">
        <motion.div
          className="flex items-center whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        >
          {duplicated.map((offer, i) => (
            <span
              key={i}
              className="text-[11px] sm:text-xs font-semibold tracking-wide flex items-center shrink-0 px-4 sm:px-5 border-r border-white/10 last:border-r-0"
            >
              {offer}
            </span>
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
}

/* ════════════════════════ HERO ════════════════════════ */

/* Animated typing text */
function TypingText() {
  const words = ["Grow.", "Scale.", "Dominate.", "Win."];
  const [wordIndex, setWordIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % words.length);
    }, 2500);
    return () => clearInterval(timer);
  }, []);

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={words[wordIndex]}
        initial={{ opacity: 0, y: 20, filter: "blur(8px)" }}
        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
        exit={{ opacity: 0, y: -20, filter: "blur(8px)" }}
        transition={{ duration: 0.5 }}
        className="text-gradient-vibrant animate-text-glow inline-block"
      >
        {words[wordIndex]}
      </motion.span>
    </AnimatePresence>
  );
}

/* Phone frame with dashboard + interactive bubble */
function PhoneMockup() {
  const mobileBarData = [35, 55, 40, 70, 50, 85, 65, 92, 75, 95];

  return (
    <motion.div
      initial={{ opacity: 0, y: 40, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay: 0.9, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-[280px] mx-auto"
    >
      {/* Phone frame */}
      <div className="relative rounded-[2.5rem] bg-slate-900 p-2 shadow-apple-xl">
        {/* Notch */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-slate-900 rounded-b-2xl z-10" />

        {/* Screen */}
        <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-b from-violet-50 to-white">
          {/* Status bar */}
          <div className="flex items-center justify-between px-6 pt-3 pb-1">
            <span className="text-[9px] font-semibold text-slate-600">
              9:41
            </span>
            <div className="flex items-center gap-1">
              <div className="w-3.5 h-2 rounded-sm border border-slate-400 relative">
                <div className="absolute inset-0.5 bg-green-500 rounded-[1px]" />
              </div>
            </div>
          </div>

          {/* Dashboard content */}
          <div className="px-3 pb-4 pt-2">
            <div className="glass-strong rounded-2xl p-3 shadow-apple border border-violet-100/50 mb-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold text-slate-700">
                  Revenue
                </span>
                <motion.span
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-[8px] text-green-600 font-bold bg-green-50 px-1.5 py-0.5 rounded-full"
                >
                  +22%
                </motion.span>
              </div>

              <div className="flex items-end gap-1 h-20 mb-2">
                {mobileBarData.map((h, i) => (
                  <motion.div
                    key={i}
                    initial={{ height: 0 }}
                    animate={{ height: `${h}%` }}
                    transition={{ delay: 1.3 + i * 0.05, duration: 0.5 }}
                    className={`flex-1 rounded-sm ${i >= 8 ? "bg-gradient-to-t from-violet-600 to-purple-400" : "bg-gradient-to-t from-violet-300/60 to-violet-100/40"}`}
                  />
                ))}
              </div>

              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { l: "Revenue", v: "₹12.5L" },
                  { l: "Leads", v: "1,247" },
                  { l: "Conv.", v: "4.8%" },
                ].map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2 + i * 0.1 }}
                    className="p-1.5 rounded-lg bg-violet-50/60 border border-violet-100/30 text-center"
                  >
                    <p className="text-[7px] text-slate-400">{m.l}</p>
                    <p className="text-[10px] font-bold text-slate-900">
                      {m.v}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Interactive bubble inside phone */}
            <InteractiveBubble />
          </div>
        </div>
      </div>

      {/* Phone shadow */}
      <div className="mt-2 mx-8 h-4 bg-black/5 rounded-full blur-xl" />
    </motion.div>
  );
}

function HeroSection() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, -120]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  /* Floating particles for extra dynamism */
  const floatingIcons = [
    {
      Icon: Sparkles,
      x: "10%",
      y: "20%",
      delay: 0,
      size: 16,
      color: "text-violet-300/40",
    },
    {
      Icon: Zap,
      x: "85%",
      y: "15%",
      delay: 1,
      size: 14,
      color: "text-purple-300/30",
    },
    {
      Icon: Star,
      x: "75%",
      y: "70%",
      delay: 2,
      size: 12,
      color: "text-indigo-300/30",
    },
    {
      Icon: Globe,
      x: "15%",
      y: "75%",
      delay: 0.5,
      size: 18,
      color: "text-violet-200/30",
    },
    {
      Icon: TrendingUp,
      x: "90%",
      y: "50%",
      delay: 1.5,
      size: 14,
      color: "text-purple-200/25",
    },
  ];

  return (
    <section className="relative min-h-[100svh] flex flex-col justify-start lg:justify-center overflow-hidden hero-mesh">
      <FloatingOrbs />
      <ElectricalCircuitBackground />
      <div className="absolute inset-0 dot-grid opacity-50" />

      {/* Dynamic floating icons — desktop only for perf */}
      <div className="hidden md:block">
        {floatingIcons.map((item, i) => (
          <motion.div
            key={i}
            className={`absolute pointer-events-none ${item.color}`}
            style={{ left: item.x, top: item.y }}
            animate={{
              y: [0, -15, 0, 15, 0],
              x: [0, 8, 0, -8, 0],
              rotate: [0, 10, 0, -10, 0],
            }}
            transition={{
              duration: 5 + i,
              delay: item.delay,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          >
            <item.Icon style={{ width: item.size, height: item.size }} />
          </motion.div>
        ))}
      </div>

      {/* ── DESKTOP HERO (lg+) ── */}
      <motion.div
        style={{ y: heroY, opacity: heroOpacity }}
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-44 pb-20 w-full hidden lg:block"
      >
        <div className="grid grid-cols-2 gap-8 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 glass rounded-full mb-8"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-500" />
              </span>
              <span className="text-sm font-medium text-slate-600">
                Now in India, Middle East & SE Asia
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.35,
                duration: 0.8,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="text-6xl lg:text-7xl font-bold tracking-tight leading-[0.95] mb-6"
            >
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.6 }}
                className="text-slate-900 inline-block"
              >
                We Build{" "}
              </motion.span>
              <br />
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="text-slate-900 inline-block"
              >
                Brands That
              </motion.span>
              <br />
              <TypingText />
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="text-xl text-slate-500 max-w-xl leading-relaxed mb-10"
            >
              Branding. Marketing. Development. AI. Everything your business
              needs to stand out, scale up, and stay ahead. All under one roof.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                className="btn-shine group inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold text-base rounded-2xl shadow-apple-lg hover:shadow-apple-xl hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
              >
                Start Your Project
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
              </Link>
              <Link
                to="/transformations"
                className="group inline-flex items-center justify-center gap-2 px-6 py-4 glass font-semibold text-slate-700 rounded-2xl hover:bg-white/70 hover:shadow-apple transition-all duration-300"
              >
                <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center group-hover:bg-violet-200 group-hover:scale-110 transition-all duration-300">
                  <Play className="w-3.5 h-3.5 text-violet-600 ml-0.5" />
                </div>
                See Our Work
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="mt-10 flex items-center gap-6 text-sm text-slate-400"
            >
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-full bg-violet-50">
                  <Shield className="w-4 h-4 text-violet-400" />
                </div>
                Trusted by 50+ businesses
              </div>
              <div className="w-px h-4 bg-slate-200" />
              <div className="flex items-center gap-2">
                <div className="p-1 rounded-full bg-violet-50">
                  <Award className="w-4 h-4 text-violet-400" />
                </div>
                Award-winning team
              </div>
            </motion.div>
          </div>

          <div>
            <HeroMockup />
          </div>
        </div>
      </motion.div>

      {/* ── MOBILE HERO (< lg) ── Clean, full-width, no phone frame ── */}
      <div className="lg:hidden relative z-10 w-full px-5 pt-10 pb-10">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex justify-center mb-6"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 glass rounded-full">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-violet-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-violet-500" />
            </span>
            <span className="text-xs font-medium text-slate-600">
              India · Middle East · SE Asia
            </span>
          </div>
        </motion.div>

        {/* Headline — centered, tight */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center text-[2.75rem] leading-[1] font-bold tracking-tight mb-4"
        >
          <span className="text-slate-900">We Build</span>
          <br />
          <span className="text-slate-900">Brands That </span>
          <br />
          <TypingText />
        </motion.h1>

        {/* Subtitle — centered */}
        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="text-center text-[15px] text-slate-500 leading-relaxed max-w-sm mx-auto mb-7"
        >
          Branding, Marketing, Development, AI. Everything to stand out and
          scale up.
        </motion.p>

        {/* CTAs — stacked, full-width buttons */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.5 }}
          className="flex flex-col gap-3 max-w-xs mx-auto mb-8"
        >
          <Link
            to="/contact"
            className="btn-shine group flex items-center justify-center gap-2.5 w-full py-3.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold text-[15px] rounded-2xl shadow-apple-lg active:scale-[0.97] transition-all"
          >
            Start Your Project
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/transformations"
            className="group flex items-center justify-center gap-2 w-full py-3.5 glass font-semibold text-slate-700 text-[15px] rounded-2xl active:scale-[0.97] hover:shadow-apple transition-all"
          >
            <Play className="w-3.5 h-3.5 text-violet-600" />
            See Our Work
          </Link>
        </motion.div>

        {/* Mini dashboard — full-width card, clean */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="bg-white/80 rounded-2xl p-4 shadow-apple-lg border border-violet-100/40 max-w-sm mx-auto"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                <BarChart3 className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-[13px] font-semibold text-slate-800">
                Dashboard
              </span>
            </div>
            <span className="text-[11px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
              +22% ↑
            </span>
          </div>

          <div className="flex items-end gap-[3px] h-20 mb-3 px-0.5">
            {[35, 55, 40, 70, 50, 85, 65, 92, 75, 95, 82, 60].map((h, i) => (
              <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${h}%` }}
                transition={{
                  delay: 1.1 + i * 0.04,
                  duration: 0.5,
                  ease: "easeOut",
                }}
                className={`flex-1 rounded-[3px] ${
                  i >= 9
                    ? "bg-gradient-to-t from-violet-600 to-purple-400"
                    : "bg-gradient-to-t from-violet-200 to-violet-100"
                }`}
              />
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {[
              { l: "Revenue", v: "₹12.5L", c: "+22%", icon: TrendingUp },
              { l: "Leads", v: "1,247", c: "+18%", icon: Users },
              { l: "Conversion", v: "4.8%", c: "+12%", icon: Zap },
            ].map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6 + i * 0.1 }}
                className="bg-violet-50/70 rounded-xl p-2 text-center border border-violet-100/30"
              >
                <m.icon className="w-3 h-3 text-violet-400 mx-auto mb-0.5" />
                <p className="text-[10px] text-slate-400 font-medium">{m.l}</p>
                <p className="text-sm font-bold text-slate-900">{m.v}</p>
                <p className="text-[9px] font-bold text-green-600">{m.c}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Trust row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="flex items-center justify-center gap-4 mt-6 text-[11px] text-slate-400"
        >
          <div className="flex items-center gap-1.5">
            <Shield className="w-3 h-3 text-violet-400" />
            50+ businesses
          </div>
          <div className="w-1 h-1 rounded-full bg-slate-300" />
          <div className="flex items-center gap-1.5">
            <Award className="w-3 h-3 text-violet-400" />
            Award-winning
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ════════════════════ SERVICES ════════════════════ */

function ServicesSection() {
  return (
    <Section className="bg-violet-tint" id="services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-6 sm:mb-10">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="inline-block text-sm font-semibold text-violet-600 tracking-widest uppercase mb-4"
          >
            What We Do
          </motion.span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-5">
            Everything Your Business{" "}
            <span className="text-gradient">Needs to Grow</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed">
            From the first logo sketch to AI-powered automation, we cover every
            stage of your digital journey.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
          {SERVICES.map((service, i) => (
            <Link key={service.title} to={service.href}>
              <MagneticCard>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    delay: i * 0.1,
                    duration: 0.5,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className={`group relative glass card-glow-top card-shine rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-apple hover:shadow-apple-xl transition-all duration-500 cursor-pointer h-full border-2 hover:border-violet-200/60 ${
                    service.tag
                      ? "border-violet-400/80 shadow-lg shadow-violet-500/30 hover:shadow-violet-500/50"
                      : "border-white/60"
                  }`}
                >
                  {service.tag && (
                    <span className="absolute top-4 right-4 sm:top-6 sm:right-6 px-3 sm:px-4 py-1 sm:py-1.5 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 text-white text-xs sm:text-sm font-bold uppercase tracking-widest rounded-full shadow-lg shadow-purple-500/50 animate-pulse-slow border border-white/30">
                      {service.tag}
                    </span>
                  )}

                  <div
                    className={`w-10 h-10 sm:w-14 sm:h-14 ${service.light} rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-5 group-hover:scale-110 transition-transform duration-300 shadow-sm`}
                  >
                    <service.icon
                      className={`w-5 h-5 sm:w-7 sm:h-7 ${service.iconColor}`}
                    />
                  </div>

                  <h3 className="text-sm sm:text-xl font-bold text-slate-900 mb-1.5 sm:mb-3">
                    {service.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 leading-relaxed mb-3 sm:mb-5 line-clamp-3 sm:line-clamp-none">
                    {service.desc}
                  </p>

                  <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-violet-600 group-hover:gap-3 transition-all duration-300">
                    Learn More
                    <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
                  </div>

                  <div
                    className={`absolute bottom-0 left-4 right-4 h-1 bg-gradient-to-r ${service.gradient} rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  />
                </motion.div>
              </MagneticCard>
            </Link>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ════════════════════ STATS ════════════════════ */

function StatsSection() {
  return (
    <Section className="py-6 sm:py-8 md:py-12 -mt-4 sm:-mt-6 md:-mt-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="glass-strong rounded-2xl sm:rounded-3xl p-4 sm:p-10 md:p-14 shadow-apple-lg overflow-hidden relative noise-overlay"
        >
          <div className="absolute inset-0 dot-grid opacity-30" />
          <div className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {STATS.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12, type: "spring", stiffness: 100 }}
                whileHover={{ scale: 1.05 }}
                className="text-center cursor-default group"
              >
                <div className="inline-flex p-2 sm:p-3 rounded-xl sm:rounded-2xl bg-gradient-to-br from-violet-50 to-purple-50 mb-2 sm:mb-4 shadow-sm group-hover:shadow-apple transition-shadow duration-300">
                  <stat.icon className="w-4 h-4 sm:w-6 sm:h-6 text-violet-600" />
                </div>
                <p className="text-xl sm:text-3xl md:text-4xl font-bold text-gradient mb-1 sm:mb-2">
                  <AnimatedCounter value={stat.value} />
                </p>
                <p className="text-[10px] sm:text-sm text-slate-500">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

/* ════════════════════ SHOWCASE / WORK ════════════════════ */

function ShowcaseSection() {
  return (
    <Section className="bg-violet-tint" id="work">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
          <div>
            <span className="text-sm font-semibold text-violet-600 tracking-widest uppercase mb-3 block">
              Our Work
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900">
              Results That <span className="text-gradient">Speak</span>
            </h2>
          </div>
          <Link
            to="/transformations"
            className="inline-flex items-center gap-2 text-sm font-semibold text-violet-600 hover:text-violet-700 transition-colors"
          >
            View All Projects
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {SHOWCASE.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.5 }}
              whileHover={{ y: -8 }}
              className="group glass rounded-3xl overflow-hidden shadow-apple hover:shadow-apple-xl transition-all duration-500 cursor-pointer card-shine border border-white/60 hover:border-violet-100/80"
            >
              {/* Real image with overlay */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover img-parallax"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/10 to-transparent group-hover:from-slate-900/70 transition-all duration-500" />
                <div className="absolute bottom-4 right-4 glass-strong rounded-2xl p-3 text-center shadow-apple group-hover:scale-105 transition-transform duration-300">
                  <p className="text-xl font-bold text-slate-900">
                    {item.stat}
                  </p>
                  <p className="text-[10px] text-slate-500 font-medium">
                    {item.statLabel}
                  </p>
                </div>
                <span className="absolute top-4 left-4 px-3 py-1.5 glass-strong rounded-full text-xs font-semibold text-slate-700 shadow-sm">
                  {item.category}
                </span>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-violet-700 transition-colors duration-300">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-3">
                  {item.desc}
                </p>
                <div className="flex items-center gap-1.5 text-sm font-semibold text-violet-600 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                  View Case Study
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ════════════════════ PROCESS ════════════════════ */

function ProcessSection() {
  return (
    <Section id="process">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-6 sm:mb-10">
          <span className="text-sm font-semibold text-violet-600 tracking-widest uppercase mb-3 block">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-5">
            Simple Process,{" "}
            <span className="text-gradient">Powerful Results</span>
          </h2>
          <p className="text-lg text-slate-500">
            A streamlined approach refined over 150+ successful projects.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {PROCESS_STEPS.map((step, i) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.5 }}
              whileHover={{ y: -6, scale: 1.03 }}
              className="relative glass card-glow-top card-shine rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-apple hover:shadow-apple-lg transition-all duration-300 group border border-white/60 hover:border-violet-200/60"
            >
              <span className="text-3xl sm:text-6xl font-black text-violet-100 absolute top-2 right-2 sm:top-4 sm:right-4 select-none group-hover:text-violet-200 transition-colors duration-300">
                {step.step}
              </span>

              <div className="relative z-10">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-50 to-purple-100 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-5 group-hover:scale-110 transition-transform duration-300 shadow-sm">
                  <step.icon className="w-4 h-4 sm:w-6 sm:h-6 text-violet-600" />
                </div>
                <h3 className="text-sm sm:text-lg font-bold text-slate-900 mb-1 sm:mb-2">
                  {step.title}
                </h3>
                <p className="text-xs sm:text-sm text-slate-500 leading-relaxed line-clamp-3 sm:line-clamp-none">
                  {step.desc}
                </p>
              </div>

              {i < PROCESS_STEPS.length - 1 && (
                <div className="hidden lg:flex absolute top-1/2 -right-4 z-10 items-center">
                  <div className="w-8 h-px bg-gradient-to-r from-violet-300 to-violet-100" />
                  <div className="w-0 h-0 border-t-[3px] border-t-transparent border-b-[3px] border-b-transparent border-l-[5px] border-l-violet-200" />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ════════════════════ TESTIMONIALS ════════════════════ */

function TestimonialsSection() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Section className="bg-violet-tint" id="testimonials">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span className="text-sm font-semibold text-violet-600 tracking-widest uppercase mb-3 block">
            Client Love
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900">
            What Our <span className="text-gradient">Clients Say</span>
          </h2>
        </div>

        <div className="max-w-3xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="glass-strong rounded-3xl p-6 sm:p-10 md:p-14 text-center shadow-apple-lg border border-white/60"
            >
              {/* Star rating */}
              <div className="flex justify-center gap-1 mb-6">
                {Array.from({ length: TESTIMONIALS[active].rating }).map(
                  (_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 fill-amber-400 text-amber-400"
                    />
                  ),
                )}
              </div>

              <blockquote className="text-base sm:text-lg md:text-xl text-slate-700 leading-relaxed mb-6 sm:mb-8 font-medium italic">
                &ldquo;{TESTIMONIALS[active].quote}&rdquo;
              </blockquote>

              {/* Avatar + name */}
              <div className="flex items-center justify-center gap-4">
                <img
                  src={TESTIMONIALS[active].avatar}
                  alt={TESTIMONIALS[active].name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-violet-100 shadow-apple"
                  loading="lazy"
                />
                <div className="text-left">
                  <p className="text-base font-bold text-slate-900">
                    {TESTIMONIALS[active].name}
                  </p>
                  <p className="text-sm text-slate-500">
                    {TESTIMONIALS[active].role}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation dots */}
          <div className="flex justify-center gap-2 mt-8">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  i === active
                    ? "bg-gradient-to-r from-violet-600 to-purple-600 w-8"
                    : "bg-violet-200 hover:bg-violet-300 w-2.5"
                }`}
              />
            ))}
          </div>

          {/* Avatar row */}
          <div className="flex justify-center gap-3 mt-6">
            {TESTIMONIALS.map((t, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`w-11 h-11 rounded-full overflow-hidden border-2 transition-all duration-300 ${
                  i === active
                    ? "border-violet-500 scale-110 shadow-apple-lg"
                    : "border-white/80 opacity-50 hover:opacity-80"
                }`}
              >
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ════════════════════ MARKETS ════════════════════ */

function MarketsSection() {
  return (
    <Section id="markets">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span className="text-sm font-semibold text-violet-600 tracking-widest uppercase mb-3 block">
            Global Presence
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-5">
            Operating in <span className="text-gradient">3 Markets</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {MARKETS.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              whileHover={{ y: -8, scale: 1.01 }}
              className="group relative rounded-3xl overflow-hidden shadow-apple hover:shadow-apple-xl transition-all duration-500 card-shine"
            >
              {/* Country image */}
              <div className="h-64 relative overflow-hidden">
                <img
                  src={m.image}
                  alt={m.name}
                  className="w-full h-full object-cover img-parallax"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/30 to-transparent group-hover:from-slate-900/90 transition-all duration-500" />
              </div>

              {/* Content overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform group-hover:translate-y-0 transition-transform duration-300">
                <div className="flex items-center gap-4 mb-2">
                  <span className="text-4xl group-hover:scale-110 transition-transform duration-300">
                    {m.badge}
                  </span>
                  <h3 className="text-xl font-bold">{m.name}</h3>
                </div>
                <p className="text-sm text-white/70">{m.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

function InsightsSection({ blogs, isLoading }) {
  const featuredBlogs = blogs.slice(0, 3);

  return (
    <Section className="bg-violet-tint" id="blog-insights">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-9 gap-4">
          <div>
            <span className="text-sm font-semibold text-violet-600 tracking-widest uppercase mb-3 block">
              BusiMag
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900">
              Latest <span className="text-gradient">Insights</span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-slate-500 max-w-xl leading-relaxed">
              Freshly published strategy notes and growth playbooks powered by
              your backend blog system.
            </p>
          </div>
          <Link
            to="/busimag"
            className="inline-flex items-center gap-2 text-sm font-semibold text-violet-600 hover:text-violet-700 transition-colors"
          >
            Read Articles
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <CardSkeleton key={i} />
            ))}
          </div>
        )}

        {!isLoading && featuredBlogs.length === 0 && (
          <EmptyState
            title="No published articles yet"
            description="Create and publish articles from the admin portal to showcase your latest insights here."
            icon="BookOpen"
          />
        )}

        {!isLoading && featuredBlogs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredBlogs.map((blog, index) => (
              <motion.article
                key={blog.slug || blog.id || blog.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -6, scale: 1.01 }}
                className="group glass rounded-3xl overflow-hidden shadow-apple hover:shadow-apple-xl transition-all duration-500 card-shine border border-white/60 hover:border-violet-100/80"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={blog.image || FALLBACK_BLOG_IMAGE}
                    alt={blog.title}
                    className="w-full h-full object-cover img-parallax transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/65 to-transparent" />
                  <span className="absolute top-4 left-4 px-3 py-1.5 glass-strong rounded-full text-xs font-semibold text-slate-700 shadow-sm">
                    {blog.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="text-lg font-bold text-slate-900 mb-2 leading-snug group-hover:text-violet-700 transition-colors line-clamp-2">
                    {blog.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-4 line-clamp-2">
                    {blog.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <BookOpen className="w-3.5 h-3.5 text-violet-600" />
                      <span>{blog.date}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{blog.readTime}</span>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

/* ════════════════════ WHY TRUST US / ANTI-BUBBLE ════════════════════ */

/* ─── Animated floating bubbles that pop ─── */
function BubbleField({ labels }) {
  const normalizedLabels =
    Array.isArray(labels) && labels.length >= 6
      ? labels
      : DEFAULT_ANTI_BUBBLE_SETTINGS.bubbleLabels;

  const bubbles = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => ({
        id: i,
        x: 5 + Math.random() * 90,
        size: 20 + Math.random() * 60,
        delay: Math.random() * 5,
        duration: 4 + Math.random() * 4,
        label: normalizedLabels[i % normalizedLabels.length],
      })),
    [normalizedLabels],
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {bubbles.map((b) => (
        <motion.div
          key={b.id}
          className="absolute flex items-center justify-center rounded-full border border-white/20"
          style={{
            left: `${b.x}%`,
            bottom: "-10%",
            width: b.size,
            height: b.size,
            background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.15), rgba(139,92,246,0.08))`,
          }}
          animate={{
            y: [0, -(300 + Math.random() * 300)],
            opacity: [0, 0.7, 0.8, 0],
            scale: [0.6, 1.1, 1.3, 0],
          }}
          transition={{
            duration: b.duration,
            delay: b.delay,
            repeat: Infinity,
            repeatDelay: Math.random() * 3,
            ease: "easeOut",
          }}
        >
          <span className="text-[8px] sm:text-[10px] text-white/40 font-medium select-none">
            {b.label}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

/* ─── "Needle popping the bubble" visual ─── */
function BubblePopVisual({ burstIntervalMs, burstDurationMs, bubbleTitle }) {
  const [isBursting, setIsBursting] = useState(false);
  const [burstKey, setBurstKey] = useState(0);

  useEffect(() => {
    let nextBurstTimer;
    let burstResetTimer;

    const safeBurstInterval = Math.max(1200, Number(burstIntervalMs) || 3200);
    const safeBurstDuration = Math.max(300, Number(burstDurationMs) || 900);

    const triggerBurst = () => {
      setBurstKey((previous) => previous + 1);
      setIsBursting(true);

      burstResetTimer = setTimeout(() => {
        setIsBursting(false);
      }, safeBurstDuration);

      nextBurstTimer = setTimeout(triggerBurst, safeBurstInterval);
    };

    nextBurstTimer = setTimeout(triggerBurst, 1200);

    return () => {
      clearTimeout(nextBurstTimer);
      clearTimeout(burstResetTimer);
    };
  }, [burstDurationMs, burstIntervalMs]);

  return (
    <div className="relative w-full max-w-[340px] sm:max-w-[420px] mx-auto aspect-square">
      {/* The big AI bubble */}
      <motion.div
        className="absolute inset-8 sm:inset-12 rounded-full"
        style={{
          background:
            "radial-gradient(circle at 35% 35%, rgba(255,255,255,0.25), rgba(139,92,246,0.1) 50%, rgba(124,58,237,0.05))",
          border: "1.5px solid rgba(255,255,255,0.2)",
          boxShadow:
            "0 0 80px rgba(139,92,246,0.2), inset 0 0 60px rgba(255,255,255,0.05)",
        }}
        animate={
          isBursting
            ? {
                scale: [1, 1.08, 0.12],
                opacity: [1, 0.9, 0],
              }
            : {
                scale: [0.98, 1.04, 1],
                opacity: 1,
              }
        }
        transition={
          isBursting
            ? { duration: 0.45, ease: "easeIn" }
            : { duration: 2.2, repeat: Infinity, ease: "easeInOut" }
        }
      >
        {/* Bubble label */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-white/30 text-xs sm:text-sm font-bold tracking-widest uppercase">
            The AI
          </span>
          <span className="text-white/50 text-2xl sm:text-4xl font-black tracking-tight">
            {bubbleTitle || "Bubble"}
          </span>
        </div>

        {/* Reflection highlight */}
        <div
          className="absolute top-4 left-6 sm:top-6 sm:left-10 w-12 sm:w-16 h-6 sm:h-8 rounded-full bg-white/20 blur-sm"
          style={{ transform: "rotate(-30deg)" }}
        />
      </motion.div>

      {/* BusiGrow needle / pin coming from outside */}
      <motion.div
        className="absolute top-[15%] right-[-5%] sm:right-[-8%] z-10"
        initial={{ x: 40, opacity: 0.9, rotate: -30 }}
        animate={
          isBursting
            ? {
                x: [-8, 12, -2],
                opacity: [1, 1, 0.92],
                rotate: [-30, -30, -30],
              }
            : { x: 0, opacity: 1, rotate: -30 }
        }
        transition={
          isBursting
            ? { duration: 0.45, ease: "easeOut" }
            : { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
        }
      >
        <div className="relative">
          {/* Pin shaft */}
          <div className="w-20 sm:w-28 h-1 bg-gradient-to-r from-violet-400 to-white rounded-full shadow-[0_0_20px_rgba(139,92,246,0.5)]" />
          {/* Pin head */}
          <div className="absolute -top-2.5 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 shadow-[0_0_30px_rgba(139,92,246,0.6)] flex items-center justify-center">
            <span className="text-white text-[7px] font-black">BG</span>
          </div>
        </div>
      </motion.div>

      {/* Shatter rings from impact point */}
      <motion.div
        className="absolute top-[18%] right-[12%] z-5"
        initial={{ scale: 0, opacity: 0 }}
        animate={
          isBursting
            ? { scale: [0, 2.8], opacity: [0.8, 0] }
            : { scale: 0, opacity: 0 }
        }
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        <div className="w-16 h-16 rounded-full border-2 border-violet-300/50" />
      </motion.div>
      <motion.div
        className="absolute top-[18%] right-[12%] z-5"
        initial={{ scale: 0, opacity: 0 }}
        animate={
          isBursting
            ? { scale: [0, 3.8], opacity: [0.55, 0] }
            : { scale: 0, opacity: 0 }
        }
        transition={{ duration: 0.85, ease: "easeOut" }}
      >
        <div className="w-16 h-16 rounded-full border border-purple-300/30" />
      </motion.div>

      {/* Sparks / fragments flying out */}
      {isBursting &&
        [...Array(10)].map((_, i) => {
          const angle = (i * 45 * Math.PI) / 180;
          return (
            <motion.div
              key={`${burstKey}-${i}`}
              className="absolute top-[22%] right-[15%] w-2 h-2 rounded-full bg-violet-300"
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                x: Math.cos(angle) * (60 + Math.random() * 40),
                y: Math.sin(angle) * (60 + Math.random() * 40),
                scale: [0, 1, 0],
                opacity: [0, 1, 0],
              }}
              transition={{ duration: 0.75, delay: i * 0.03, ease: "easeOut" }}
            />
          );
        })}
    </div>
  );
}

function WhyTrustUsSection({ settings }) {
  const analysisSteps = [
    {
      icon: Search,
      title: "What You Need",
      desc: "Deep-dive research into your market, audience, and goals. We track what works and act on insights, no assumptions.",
      color: "from-violet-500 to-purple-500",
    },
    {
      icon: Brain,
      title: "Why You Need It",
      desc: "We uncover the strategic reasoning behind every decision, ensuring purpose-driven execution.",
      color: "from-purple-500 to-indigo-500",
    },
    {
      icon: Crosshair,
      title: "How You Need It",
      desc: "Custom-tailored delivery. Right format, right time, right channel.",
      color: "from-indigo-500 to-violet-500",
    },
  ];

  return (
    <Section className="overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="inline-block text-sm font-semibold text-violet-600 tracking-widest uppercase mb-4"
          >
            {settings.eyebrow}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-5"
          >
            {settings.headingPrefix}{" "}
            <span className="text-gradient-vibrant">
              {settings.headingHighlight}
            </span>
            <br />
            <span className="text-slate-900">{settings.headingSuffix}</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-base sm:text-lg text-slate-500 leading-relaxed max-w-2xl mx-auto"
          >
            {settings.description}
          </motion.p>
        </div>

        {/* Two-column layout: Analysis cards + Bubble visual */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12 sm:mb-16">
          {/* Left: Analysis approach */}
          <div className="space-y-5 sm:space-y-6">
            {analysisSteps.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 + i * 0.15, duration: 0.5 }}
                whileHover={{ x: 8, scale: 1.01 }}
                className="group relative glass card-glow-top rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-apple hover:shadow-apple-lg transition-all duration-300"
              >
                <div className="flex items-start gap-4 sm:gap-5">
                  <div
                    className={`shrink-0 w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br ${step.color} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                  >
                    <step.icon className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1.5">
                      {step.title}
                    </h3>
                    <p className="text-sm sm:text-base text-slate-500 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>

                {/* Connecting line */}
                {i < analysisSteps.length - 1 && (
                  <div className="absolute -bottom-3 left-9 sm:left-11 w-px h-5 bg-gradient-to-b from-violet-200 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>

          {/* Right: Bubble pop visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-slate-900 via-violet-950 to-indigo-950 p-6 sm:p-10 shadow-apple-xl">
              {/* Floating AI-hype bubbles */}
              <BubbleField labels={settings.bubbleLabels} />

              {/* Main visual */}
              <BubblePopVisual
                burstIntervalMs={settings.burstIntervalMs}
                burstDurationMs={settings.burstDurationMs}
                bubbleTitle={settings.headingHighlight}
              />

              {/* Bottom tagline */}
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 2 }}
                className="relative z-10 text-center text-white/60 text-xs sm:text-sm font-medium mt-4"
              >
                {settings.taglineTop}
                <br />
                <span className="text-violet-300 font-bold">
                  {settings.taglineBottom}
                </span>
              </motion.p>
            </div>
          </motion.div>
        </div>

        {/* Bottom: compelling stats / proof row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="glass-strong rounded-2xl sm:rounded-3xl p-6 sm:p-10 shadow-apple-lg"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: Eye,
                stat: "360°",
                label: "Analysis Approach",
                desc: "We study your business from every angle before writing a single line.",
              },
              {
                icon: Target,
                stat: "Zero",
                label: "Cookie-Cutter Solutions",
                desc: "Every strategy is custom-built for your unique needs and market.",
              },
              {
                icon: CircleDot,
                stat: "100%",
                label: "Purpose-Driven",
                desc: "No AI hype. No filler. Just strategies that deliver real results.",
              },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.12, duration: 0.5 }}
                className="text-center sm:text-left"
              >
                <div className="inline-flex p-3 rounded-2xl bg-gradient-to-br from-violet-50 to-purple-50 mb-3 shadow-sm">
                  <item.icon className="w-6 h-6 text-violet-600" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-gradient mb-1">
                  {item.stat}
                </p>
                <p className="text-sm font-semibold text-slate-700 mb-1">
                  {item.label}
                </p>
                <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

/* ════════════════════ FINAL CTA ════════════════════ */

function FinalCTA() {
  return (
    <Section>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-2xl sm:rounded-[2rem] bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-8 sm:p-12 md:p-20 text-center shadow-apple-xl"
        >
          {/* Decorative elements */}
          <div className="absolute -top-20 -left-20 w-40 sm:w-60 h-40 sm:h-60 rounded-full bg-white/10 blur-2xl animate-float-orb" />
          <div className="absolute -bottom-20 -right-20 w-60 sm:w-80 h-60 sm:h-80 rounded-full bg-white/5 blur-3xl animate-float-orb-reverse" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] rounded-full bg-white/[0.03] blur-[80px]" />

          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <motion.div
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="inline-flex p-4 rounded-full bg-white/10 backdrop-blur-xl mb-6"
              >
                <Sparkles className="w-8 h-8 text-white/80" />
              </motion.div>

              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-5 leading-tight">
                Ready to Grow Your Business?
              </h2>
              <p className="text-base sm:text-lg text-white/70 max-w-xl mx-auto mb-8 sm:mb-10 leading-relaxed">
                Let&apos;s create something extraordinary together. From
                branding to AI, we&apos;ve got every angle covered.
              </p>

              <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-3 sm:gap-4">
                <Link
                  to="/contact"
                  className="btn-shine group inline-flex items-center justify-center gap-3 px-6 sm:px-8 py-3.5 sm:py-4 bg-white text-violet-700 font-semibold rounded-2xl hover:bg-violet-50 hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 shadow-apple-lg"
                >
                  Get Free Consultation
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 sm:py-4 border border-white/30 text-white font-semibold rounded-2xl hover:bg-white/10 hover:border-white/50 backdrop-blur-xl active:scale-[0.97] transition-all duration-300"
                >
                  View Pricing
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

/* ════════════════════ MAIN PAGE ════════════════════ */

export default function HomePage() {
  const [introComplete, setIntroComplete] = useState(false);

  // Handle smooth scroll to hash anchors
  useScrollToHash();

  // Fetch blogs and settings using React Query hooks
  const { data: blogsData = { items: [] }, isLoading: blogInsightsLoading } =
    usePublishedBlogs({
      page: 1,
      limit: 3,
    });

  const { data: homeContentData } = useHomeContent();

  const blogInsights = blogsData.items || [];
  const antiBubbleSettings = homeContentData
    ? {
        ...DEFAULT_ANTI_BUBBLE_SETTINGS,
        ...homeContentData,
      }
    : DEFAULT_ANTI_BUBBLE_SETTINGS;

  /* Re-show intro when logo is clicked (hides page briefly) */
  useEffect(() => {
    const handleReplay = () => setIntroComplete(false);
    window.addEventListener("busigrow:replay-intro", handleReplay);
    return () =>
      window.removeEventListener("busigrow:replay-intro", handleReplay);
  }, []);

  return (
    <>
      <IntroAnimation onComplete={() => setIntroComplete(true)} />
      <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
        <Header />
        <HeroSection />
        <OfferRibbon />
        <ServicesSection />
        <StatsSection />
        <WhyTrustUsSection settings={antiBubbleSettings} />
        <ShowcaseSection />
        <ProcessSection />
        <TestimonialsSection />
        <MarketsSection />
        <InsightsSection blogs={blogInsights} isLoading={blogInsightsLoading} />
        <FinalCTA />
        <Footer />
      </div>
    </>
  );
}
